import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  BUNDESLAENDER,
  type Standort,
  type Standortfelder,
  standortAendern,
  standortAnlegen,
  standorte as holeStandorte,
} from '../api/standorte';
import { Dialog } from '../komponenten/Dialog';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { useKopfhandlungen, useZaehler, zaehlwort } from '../komponenten/Seitenkontext';

/**
 * Die Standorte des Hauses (RES-01, TAG-01, FRS-08; Auftrag #12).
 *
 * ============================================================================
 * DIE KENNUNG STEHT DA, ABER SIE LÄSST SICH NICHT ÄNDERN
 * ============================================================================
 *
 * An ihr hängen Preise (AA-011), Termine, Betriebstage, Raster und
 * Schließtage. Was der Kunde liest — die Bezeichnung, die über die
 * Buchungsschnittstelle an jedem Termin steht (BUC-18) —, ist davon getrennt
 * und frei änderbar. Eine neue Kennung gibt es nur beim Anlegen.
 *
 * ============================================================================
 * ABSCHALTEN STATT LÖSCHEN
 * ============================================================================
 *
 * Ein Standort, an dem je ein Termin stand, gehört zu dessen Geschichte.
 * Abgeschaltet fällt er aus jeder Auswahl; der Server weist das Abschalten
 * ab, solange dort noch Termine anstehen.
 *
 * ============================================================================
 * SEITENTYP LISTE (ÄA-41)
 * ============================================================================
 *
 * Zähler und „Standort anlegen" im Seitenkopf, darunter die Rastertabelle in
 * einer Karte. Eine Suche gibt es nicht: Das Haus hat eine Handvoll
 * Standorte, und ein Suchfeld über fünf Zeilen wäre eine Handlung ohne Zweck.
 */
export function Standorte({ darfPflegen }: { readonly darfPflegen: boolean }): JSX.Element {
  const [liste, setzeListe] = useState<readonly Standort[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [meldung, setzeMeldung] = useState('');

  /** Welcher Standort im Fenster steht; `neu` heißt: ein neuer. */
  const [offen, setzeOffen] = useState<Standort | 'neu' | null>(null);
  const [formular, setzeFormular] = useState<Formular>(LEER);
  const [laeuft, setzeLaeuft] = useState(false);

  const laden = useCallback(async (): Promise<void> => {
    setzeLaedt(true);
    try {
      setzeListe(await holeStandorte());
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  function oeffne(standort: Standort | 'neu'): void {
    setzeMeldung('');
    setzeFehler(undefined);
    setzeOffen(standort);
    setzeFormular(standort === 'neu' ? LEER : ausStandort(standort));
  }

  /* ÄA-41: Zähler und Haupthandlung im Seitenkopf. */
  useZaehler(laedt ? undefined : zaehlwort(liste.length, 'Standort', 'Standorte'));
  useKopfhandlungen(
    darfPflegen ? (
      <button className="knopf knopf--haupt" type="button" onClick={() => oeffne('neu')}>
        Standort anlegen
      </button>
    ) : undefined,
    [darfPflegen],
  );

  const istHauptstandort = offen !== null && offen !== 'neu' && offen.hauptstandort;

  async function absenden(ereignis: React.FormEvent): Promise<void> {
    ereignis.preventDefault();
    if (offen === null) return;
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      const felder: Standortfelder = {
        bezeichnung: formular.bezeichnung.trim(),
        strasse: leerAlsNull(formular.strasse),
        plz: leerAlsNull(formular.plz),
        ort: leerAlsNull(formular.ort),
        bundesland: formular.bundesland,
        bemerkung: leerAlsNull(formular.bemerkung),
        buchbar: formular.buchbar,
        aktiv: formular.aktiv,
      };
      if (offen === 'neu') {
        const neu = await standortAnlegen({ ...felder, kennung: formular.kennung.trim() });
        setzeMeldung(`Standort ${neu.kennung} angelegt.`);
      } else {
        const neu = await standortAendern(offen.kennung, {
          ...felder,
          googleStandort: leerAlsNull(formular.googleStandort),
        });
        setzeMeldung(`Standort ${neu.kennung} gespeichert: ${neu.bezeichnung}.`);
      }
      setzeOffen(null);
      await laden();
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <>
      {fehler !== undefined && <Meldung fehler={fehler} />}
      {meldung !== '' && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <Liste
        hinweise={
          <p className="hilfe">
            Die Bezeichnung steht über die Buchungsschnittstelle an jedem Termin des Standorts. Das
            Bundesland bestimmt die Feiertage.
          </p>
        }
      >
        {laedt ? (
          <p className="liste__leer" role="status">
            Wird geladen …
          </p>
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Standorte">
              <thead>
                <tr>
                  <th scope="col">Bezeichnung</th>
                  <th scope="col">Kennung</th>
                  <th scope="col">Anschrift</th>
                  <th scope="col">Bundesland</th>
                  <th scope="col">Stand</th>
                  <th scope="col">Betriebstag</th>
                  {darfPflegen && <th scope="col">Handlung</th>}
                </tr>
              </thead>
              <tbody>
                {liste.map((s) => (
                  <tr key={s.kennung}>
                    <th scope="row">
                      {s.bezeichnung}
                      {s.hauptstandort && <span className="hilfe"> · Hauptstandort</span>}
                    </th>
                    <td className="liste__nummer">
                      <code>{s.kennung}</code>
                    </td>
                    <td>{anschrift(s)}</td>
                    <td>{BUNDESLAENDER[s.bundesland] ?? s.bundesland}</td>
                    <td>
                      {/* Farbe ist nie das einzige Merkmal: Der Stand steht als Wort da (WCAG 1.4.1). */}
                      {s.aktiv ? (s.buchbar ? 'aktiv, buchbar' : 'aktiv, nicht buchbar') : 'abgeschaltet'}
                    </td>
                    <td>
                      {s.tagesbeginn}–{s.tagesende} · Raster {s.terminrasterMinuten} min
                    </td>
                    {darfPflegen && (
                      <td>
                        <button className="knopf knopf--klein" type="button" onClick={() => oeffne(s)}>
                          Bearbeiten
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>

      {offen !== null && (
        <Dialog
          titel={offen === 'neu' ? 'Neuer Standort' : `Standort ${offen.kennung}`}
          unterzeile={offen === 'neu' ? undefined : offen.bezeichnung}
          breite="breit"
          beiSchliessen={() => setzeOffen(null)}
        >
          <form noValidate onSubmit={(e) => void absenden(e)}>
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stKennung">
                Kennung {offen === 'neu' && <span className="feld__pflicht">Pflicht</span>}
              </label>
              <input
                id="stKennung"
                className="feld__eingabe"
                value={formular.kennung}
                readOnly={offen !== 'neu'}
                aria-describedby="stKennungHilfe"
                onChange={(e) => {
                  setzeFormular({ ...formular, kennung: e.target.value.toUpperCase() });
                }}
              />
              <p className="feld__hilfe" id="stKennungHilfe">
                {offen === 'neu'
                  ? 'Großbuchstaben, Ziffern und Unterstrich, etwa HANNOVER. Sie lässt sich später '
                    + 'nicht mehr ändern — an ihr hängen Preise, Termine und Betriebstage.'
                  : 'Unveränderlich: An ihr hängen Preise, Termine, Betriebstage und Raster.'}
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stBezeichnung">
                Bezeichnung <span className="feld__pflicht">Pflicht</span>
              </label>
              <input
                id="stBezeichnung"
                className="feld__eingabe"
                value={formular.bezeichnung}
                onChange={(e) => {
                  setzeFormular({ ...formular, bezeichnung: e.target.value });
                }}
              />
              <p className="feld__hilfe">
                So erscheint der Standort in Auswahllisten, im Aushang und in der Buchungsschnittstelle.
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stStrasse">
                Straße und Hausnummer
              </label>
              <input
                id="stStrasse"
                className="feld__eingabe"
                value={formular.strasse}
                onChange={(e) => {
                  setzeFormular({ ...formular, strasse: e.target.value });
                }}
              />
            </div>
            <div className="feldpaar">
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="stPlz">
                  Postleitzahl
                </label>
                <input
                  id="stPlz"
                  className="feld__eingabe"
                  inputMode="numeric"
                  value={formular.plz}
                  onChange={(e) => {
                    setzeFormular({ ...formular, plz: e.target.value });
                  }}
                />
              </div>
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="stOrt">
                  Ort
                </label>
                <input
                  id="stOrt"
                  className="feld__eingabe"
                  value={formular.ort}
                  onChange={(e) => {
                    setzeFormular({ ...formular, ort: e.target.value });
                  }}
                />
              </div>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stBundesland">
                Bundesland <span className="feld__pflicht">Pflicht</span>
              </label>
              <select
                id="stBundesland"
                className="feld__eingabe"
                value={formular.bundesland}
                onChange={(e) => {
                  setzeFormular({ ...formular, bundesland: e.target.value });
                }}
              >
                {Object.entries(BUNDESLAENDER).map(([kuerzel, name]) => (
                  <option key={kuerzel} value={kuerzel}>
                    {name}
                  </option>
                ))}
              </select>
              {/* FRS-08 */}
              <p className="feld__hilfe">
                Bestimmt die gesetzlichen Feiertage des Standorts — und damit Zahlungsfristen,
                Arbeitstage und Servicezeiten.
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stBemerkung">
                Bemerkung
              </label>
              <textarea
                id="stBemerkung"
                className="feld__eingabe"
                rows={2}
                value={formular.bemerkung}
                onChange={(e) => {
                  setzeFormular({ ...formular, bemerkung: e.target.value });
                }}
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stBuchbar">
                Buchbar
              </label>
              <input
                id="stBuchbar"
                type="checkbox"
                checked={formular.buchbar}
                onChange={(e) => {
                  setzeFormular({ ...formular, buchbar: e.target.checked });
                }}
              />
              {/*
                BUC-18 i. d. F. AeA-67: Das Kennzeichen wirkt nur auf die
                Buchungsschnittstelle. Im CRM bleibt der Standort buchbar.
              */}
              <p className="feld__hilfe">
                Online buchbar. Ohne Häkchen zeigt das Buchungsportal den Standort und
                seine Termine nicht an und nimmt dort keine Reservierung an. Im CRM
                bleiben die Termine buchbar.
              </p>
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="stAktiv">
                Aktiv
              </label>
              {/* ANG-27: Der Hauptstandort bleibt eingeschaltet — der Server weist es sonst ab. */}
              <input
                id="stAktiv"
                type="checkbox"
                checked={formular.aktiv}
                disabled={istHauptstandort && formular.aktiv}
                onChange={(e) => {
                  setzeFormular({ ...formular, aktiv: e.target.checked });
                }}
              />
              <p className="feld__hilfe">
                {istHauptstandort
                  ? 'Der Hauptstandort bleibt eingeschaltet: An ihm entstehen die Termine '
                    + 'angenommener Exklusivangebote.'
                  : 'Abgeschaltet verschwindet der Standort aus Terminmaske, Trainingsplanung und '
                    + 'Kalender. Das geht erst, wenn dort keine Termine mehr anstehen.'}
              </p>
            </div>

            {offen !== 'neu' && (
              <div className="feld">
                <label className="feld__beschriftung" htmlFor="stGoogle">
                  Google-Standort
                </label>
                <input
                  id="stGoogle"
                  className="feld__eingabe"
                  value={formular.googleStandort}
                  placeholder="accounts/…/locations/…"
                  onChange={(e) => {
                    setzeFormular({ ...formular, googleStandort: e.target.value });
                  }}
                />
                {/* SUP-51: die Zuordnung, gegen die die Rezensionen abgeglichen werden. */}
                <p className="feld__hilfe">
                  Der Name des Standorts im Google-Unternehmensprofil. Leer: Für diesen Standort
                  werden keine Google-Rezensionen abgeglichen.
                </p>
              </div>
            )}

            {offen === 'neu' && (
              /* TAG-01 i.d.F. AeA-07, AeA-26 */
              <p className="hinweiszeile">
                Der Betriebstag beginnt um 08:00 und endet um 17:00, geteilt in Blöcke zu 15
                Minuten; Trainings beginnen im Raster von 45 Minuten.
              </p>
            )}

            <div className="feldreihe">
              <button className="knopf knopf--haupt" type="submit" disabled={laeuft}>
                {offen === 'neu' ? 'Anlegen' : 'Speichern'}
              </button>{' '}
              <button className="knopf knopf--verweis" type="button" onClick={() => setzeOffen(null)}>
                Abbrechen
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </>
  );
}

function anschrift(s: Standort): string {
  const zeile = [s.plz, s.ort].filter((t) => t !== null).join(' ');
  return [s.strasse, zeile].filter((t) => t !== null && t !== '').join(', ') || '—';
}

/** Das Formular führt Text; leer heißt „nicht angegeben". */
type Formular = {
  kennung: string;
  bezeichnung: string;
  strasse: string;
  plz: string;
  ort: string;
  bundesland: string;
  bemerkung: string;
  buchbar: boolean;
  aktiv: boolean;
  googleStandort: string;
};

const LEER: Formular = {
  kennung: '',
  bezeichnung: '',
  strasse: '',
  plz: '',
  ort: '',
  bundesland: 'NI',
  bemerkung: '',
  buchbar: true,
  aktiv: true,
  googleStandort: '',
};

function ausStandort(s: Standort): Formular {
  return {
    kennung: s.kennung,
    bezeichnung: s.bezeichnung,
    strasse: s.strasse ?? '',
    plz: s.plz ?? '',
    ort: s.ort ?? '',
    bundesland: s.bundesland,
    bemerkung: s.bemerkung ?? '',
    buchbar: s.buchbar,
    aktiv: s.aktiv,
    googleStandort: s.googleStandort ?? '',
  };
}

function leerAlsNull(wert: string): string | null {
  const geputzt = wert.trim();
  return geputzt === '' ? null : geputzt;
}
